<template>
  <refonte-modal
    v-bind:show="props.show"
    v-on:close="props.on_close"
    class="!max-w-[800px]">
    <template v-slot:modalHeader>
      <refonte-select-signataire-header
        v-bind:title="props.header_title"
        v-bind:formation_title="props.formation_title"
        v-bind:session_date="props.session_date"
        v-bind:participant_nom_prenom="props.participant_nom_prenom" />
    </template>
    <template v-slot:modalBody>
      <div class="flex flex-col gap-[16px] pb-[12px]">
        <refonte-select-signataire-body-informations />
        <template v-if="signatairesLoading">
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>
        <template v-else>
          <refonte-select-signataire-body-signataire-choice
            v-if="!signataireUnique"
            document_name="de la convention"
            v-bind:has_admin_rights="hasAdminRights"
            v-bind:signataire_list="signataireList"
            v-bind:selected_signataire="selectedSignataire"
            v-bind:select_signataire="selectSignataire"
            v-bind:open_signature_manuelle="props.open_signature_manuelle"
            v-bind:client_id="props.client_id" />
          <refonte-select-signataire-body-selected-signataire
            v-if="selectedSignataire"
            document_name="de la convention"
            v-bind:signataire_phone_valid="signatairePhoneValid"
            v-bind:selected_signataire="selectedSignataire"
            v-bind:open_signature_manuelle="props.open_signature_manuelle"
            v-bind:signature_unique="signataireUnique"
            v-bind:has_admin_rights="hasAdminRights"
            v-bind:client_id="props.client_id" />
        </template>
      </div>
    </template>
    <template v-slot:modalFooter>
      <refonte-select-signataire-footer
        v-bind:cancel="props.on_close"
        v-bind:submit="submit"
        v-bind:submit_disabled="
          !selectedSignataire || !signatairePhoneValid || buttonLoading
        " />
    </template>
  </refonte-modal>
  <refonte-modal-confirmation-action
    v-if="selectedSignataire"
    v-bind:show="isErrorModalOpen"
    v-bind:on_close="closeErrorModal"
    title="Erreur lors du lancement de la procédure"
    content="Une erreur est survenue lors du lancement de la procédure de signature. Veuillez réeesayer ultérieurement ou contacter votre administrateur." />
  <refonte-modal-file-select-signataire-success
    v-if="selectedSignataire"
    v-bind:show="isConfirmationActionDoneModalOpen"
    v-bind:on_close="closeConfirmationActionDoneModal"
    v-bind:signataire="selectedSignataire"
    v-bind:title="props.formation_title" />
</template>

<script setup lang="ts">
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import { computed, ComputedRef, onMounted, ref } from 'vue';
  import { InterlocuteurSignataire } from '@/types/gestion/interlocuteurs-types';
  import { Entreprise } from '@/types/gestion/entreprise';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import { storeToRefs } from 'pinia';
  import RefonteModalConfirmationAction from '@/components/refonte/modals/refonte-modal-confirmation-action.vue';
  import { API } from '@/http-common';
  import RefonteModalFileSelectSignataireSuccess from '@/components/refonte/modals/refonte-modal-file-select-signataire-success.vue';
  import spinner from '@/components/utils/spinner.vue';
  import { isPhoneValid } from '@/mixin/utils/phone_utils';
  import RefonteSelectSignataireHeader from '@/components/refonte/modals/signataire/refonte-select-signataire-header.vue';
  import RefonteSelectSignataireFooter from '@/components/refonte/modals/signataire/refonte-select-signataire-footer.vue';
  import RefonteSelectSignataireBodyInformations from '@/components/refonte/modals/signataire/refonte-select-signataire-body-informations.vue';
  import RefonteSelectSignataireBodySignataireChoice from '@/components/refonte/modals/signataire/refonte-select-signataire-body-signataire-choice.vue';
  import RefonteSelectSignataireBodySelectedSignataire from '@/components/refonte/modals/signataire/refonte-select-signataire-body-selected-signataire.vue';

  const props = withDefaults(
    defineProps<{
      header_title?: string;
      show: boolean;
      on_close: Function;
      on_submit: Function;
      title?: string;
      file_title: string;
      formation_title?: string;
      session_date?: string;
      participant_nom_prenom?: string;
      client_id?: number;
      launch_url?: string;
      class?: string;
      open_signature_manuelle?: Function;
    }>(),
    {
      class: ''
    }
  );
  const signatairesLoading = ref<boolean>(true);

  const entrepriseStore = useEntrepriseStore();

  const { entreprises } = storeToRefs(entrepriseStore);

  const hasAdminRights: ComputedRef<boolean> = computed(() => {
    const entreprise = entreprises.value.find(
      (e: Entreprise) => e.entrepriseId == props.client_id
    ) as Entreprise | undefined;
    return !!entreprise?.droitAdministrateur;
  });

  const signatairePhoneValid = ref<boolean>(false);
  const signataireUnique = ref<boolean>(false);
  const signataireList = ref<InterlocuteurSignataire[]>([]);

  const buttonLoading = ref<boolean>(false);
  const submit = async () => {
    buttonLoading.value = true;
    await envoyerSignature();
    buttonLoading.value = false;
  };

  const selectedSignataire = ref<InterlocuteurSignataire>();
  const isConfirmationActionDoneModalOpen = ref<boolean>(false);
  const isErrorModalOpen = ref<boolean>(false);

  const selectSignataire = (signataire: InterlocuteurSignataire) => {
    selectedSignataire.value = signataire;
    signatairePhoneValid.value = isPhoneValid(signataire.telephone);
  };

  const closeErrorModal = () => {
    isErrorModalOpen.value = false;
    props.on_close();
  };

  const closeConfirmationActionDoneModal = () => {
    isConfirmationActionDoneModalOpen.value = false;
    props.on_close();
    if (props.on_submit) {
      props.on_submit();
    }
  };

  onMounted(async () => {
    signatairesLoading.value = true;
    await fetchSignataires();
    signatairesLoading.value = false;
  });

  const loading = ref<boolean>(false);

  const envoyerSignature = async () => {
    loading.value = true;
    if (props.client_id && props.launch_url) {
      try {
        const response = await API.post(
          props.launch_url,
          { signataireId: selectedSignataire.value?.contactId },
          { headers: { 'SKIP-REDIRECTION': true } }
        );
        if (response.status == 200) {
          isConfirmationActionDoneModalOpen.value = true;
        } else {
          isErrorModalOpen.value = true;
        }
      } catch (e) {
        isErrorModalOpen.value = true;
      }
    }

    loading.value = false;
  };
  const fetchSignataires = async () => {
    loading.value = true;
    if (props.client_id) {
      const response = await API.get(
        `client/v2/${props.client_id}/interlocuteurs/signataires`
      );
      if (response && response.data) {
        signataireUnique.value = response.data.forceUniqueSignataire;
        if (response.data.signataire) {
          selectSignataire({
            ...response.data.signataire,
            nomPrenom: `${response.data.signataire.nom} ${response.data.signataire.prenom}`
          });
        }

        signataireList.value = response.data.signataires
          ? response.data.signataires.map((i: InterlocuteurSignataire) => ({
              ...i,
              nomPrenom: `${i.nom} ${i.prenom}`
            }))
          : [];
      }
    }
    loading.value = false;
  };
</script>

<style lang="scss" scoped></style>
